import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Gradients = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🎨 Advanced CSS: Gradients</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS <b>gradients</b> let you create smooth transitions between two or
        more colors, without needing images. They are rendered by the browser,
        making them lightweight, scalable, and highly customizable for modern
        UIs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Gradients are created with <code>linear-gradient</code>,{" "}
            <code>radial-gradient</code>, and <code>conic-gradient</code>.
          </li>
          <li>They support multiple color stops with custom positions.</li>
          <li>
            Gradients can be repeated using{" "}
            <code>repeating-linear-gradient</code>, etc.
          </li>
          <li>Can be combined with images for overlays and effects.</li>
          <li>
            They are resolution-independent and render smoothly at any size.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Linear Gradient */
background: linear-gradient(direction, color1, color2, ...);

/* Radial Gradient */
background: radial-gradient(shape size at position, color1, color2, ...);

/* Conic Gradient */
background: conic-gradient(from angle at position, color1, color2, ...);`}
        />
      </div>

      {/* Linear Gradient Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Linear Gradients</h2>
        <p>
          Linear gradients transition colors along a line (horizontal, vertical,
          diagonal, or custom angle).
        </p>
        <div className="gradient-demo">
          <div
            style={{
              background: "linear-gradient(to right, #f87171, #60a5fa)",
              height: "100px",
              borderRadius: "8px",
              marginBottom: "1rem",
            }}
          ></div>
        </div>
        <CodeCopyBlock
          code={`div {
  background: linear-gradient(to right, #f87171, #60a5fa);
}`}
        />
      </div>

      {/* Radial Gradient Example */}
      <div className="tutorial-section">
        <h2 className="section-title">☀️ Radial Gradients</h2>
        <p>
          Radial gradients spread outward from a center point, forming circular
          or elliptical shapes.
        </p>
        <div
          style={{
            background:
              "radial-gradient(circle at center, #facc15, #f97316, #dc2626)",
            height: "100px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: radial-gradient(circle at center, #facc15, #f97316, #dc2626);
}`}
        />
      </div>

      {/* Conic Gradient Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🎯 Conic Gradients</h2>
        <p>
          Conic gradients rotate around a center point, creating pie-like
          patterns.
        </p>
        <div
          style={{
            background:
              "conic-gradient(from 90deg at 50% 50%, #4ade80, #3b82f6, #a855f7, #f43f5e, #4ade80)",
            height: "100px",
            borderRadius: "50%",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: conic-gradient(from 90deg at 50% 50%, #4ade80, #3b82f6, #a855f7, #f43f5e, #4ade80);
}`}
        />
      </div>

      {/* Repeating Gradient Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🔁 Repeating Gradients</h2>
        <p>
          Repeating gradients repeat a gradient pattern infinitely in the given
          direction.
        </p>
        <div
          style={{
            background:
              "repeating-linear-gradient(45deg, #22d3ee, #22d3ee 20px, #818cf8 20px, #818cf8 40px)",
            height: "100px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: repeating-linear-gradient(
    45deg,
    #22d3ee,
    #22d3ee 20px,
    #818cf8 20px,
    #818cf8 40px
  );
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        className="tutorial-section"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 className="section-title">🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <code>background-blend-mode</code> to combine
            gradients and images.
          </li>
          <li>
            Use gradients in buttons, cards, and text effects for modern UI
            design.
          </li>
          <li>
            Try <code>transparent</code> in gradients to create overlays.
          </li>
          <li>Combine multiple gradients for layered backgrounds.</li>
          <li>Explore CSS Houdini Paint API for custom gradient effects.</li>
        </ul>
      </div>
    </div>
  );
};

export default CSS_Gradients;
